import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Tables, TablesUpdate } from '@/integrations/supabase/types';

type Profile = Tables<'profiles'>;
type ProfileUpdate = TablesUpdate<'profiles'>;

export const useProfile = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const profileQueryKey = ['profile', user?.id] as const;

  const { data: profile, isLoading, error } = useQuery({
    queryKey: profileQueryKey,
    queryFn: async () => {
      if (!user) return null;
      
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .maybeSingle();
      
      if (error) throw error;
      return data as Profile;
    },
    enabled: !!user,
  });

  const updateProfile = useMutation({
    mutationFn: async (updates: ProfileUpdate) => {
      if (!user) throw new Error('Not authenticated');
      
      const { data, error } = await supabase
        .from('profiles')
        .update(updates)
        .eq('id', user.id)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: profileQueryKey });
      
      // Log profile update activity
      if (user) {
        supabase.from('user_activity_logs').insert([{
          user_id: user.id,
          action_type: 'profile_update',
          description: 'Profile information was updated',
          user_agent: navigator.userAgent,
        }]);
      }
    },
  });

  return {
    profile,
    isLoading,
    error,
    updateProfile,
    refreshProfile: async () => {
      if (!user) return null;

      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .maybeSingle();

      if (error) throw error;

      queryClient.setQueryData(profileQueryKey, data as Profile | null);
      await queryClient.invalidateQueries({ queryKey: profileQueryKey, refetchType: 'active' });

      return data as Profile | null;
    },
  };
};
